<script setup lang="ts">
import { computed } from "vue";
import { addMinutes, isBefore } from "date-fns";
import TimePickerDropdown from "~/components/Common/Dropdowns/TimePickerDropdown.vue";
import { handleValueInput } from "~/utils/helpers/handleValueInput";

const handleValue = handleValueInput();
const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  defaultText: {
    type: String,
    default: "--:--",
  },
  errorMessage: {
    type: String,
    default: null,
  },
  error: {
    type: Boolean,
    default: false,
  },
  interval: {
    type: Number,
    default: 15,
  },
  duration: {
    type: Number,
    default: 24,
  },
  modelValue: {
    type: String,
    default: null,
  },
  minDate: {
    type: Date,
    default: null,
  },
  maxDate: {
    type: Date,
    default: null,
  },
  minTime: {
    type: Number,
    default: null,
  },
  maxTime: {
    type: Number,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  defaultValue: {
    type: String,
    default: undefined,
  },
});

const selectedTimeFormatted = computed({
  get() {
    const { modelValue } = props;
    return typeof modelValue === "string" ? modelValue : null;
  },
  set(val) {
    emit("update:modelValue", val);
  },
});

const search = ref<string>("");

const availableTimes = computed(() => {
  const { duration, interval } = props;
  const start = day();
  const end = addMinutes(start, duration * 60);
  const times = [];
  let current = start;

  while (isBefore(current, end)) {
    const formattedTime = handleValue.formatTime(current);
    if (!isTimeDisabled(formattedTime)) {
      times.push(formattedTime);
    }
    current = addMinutes(current, interval);
  }

  // Add input time if not present in list
  if (!times.some((s) => s.includes(search.value))) {
    const format = formatSearch(search.value);
    format ? times.push(search.value) : null;
  }

  return times;
});

function formatSearch(input: string) {
  const value = input.trim();

  if (/^\d{1,2}(:\d{0,2})?$/.test(value)) {
    const parts = value.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parts[1] ? parseInt(parts[1]) : 0;

    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      const formattedHours = hours.toString().padStart(2, "0");
      const formattedMinutes = minutes.toString().padStart(2, "0");
      return formattedHours + ":" + formattedMinutes;
    }
  }

  return "";
}

function day() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function isTimeDisabled(time: string) {
  const { minTime, maxTime } = props;
  let { minDate, maxDate } = props;
  if (!minTime && !minDate && !maxDate && !maxTime) return false;

  const t = handleValue.parseToDate(time);

  const min = new Date();
  const max = new Date();
  min.setHours(0, 0, 0, 0);
  max.setHours(24, 0, 0, 0);

  if (minDate) {
    minDate = new Date(minDate);
    min.setHours(minDate.getHours(), minDate.getMinutes());
  }
  if (maxDate) {
    maxDate = new Date(maxDate);
    max.setHours(maxDate.getHours(), maxDate.getMinutes());
  }

  if (minTime) {
    min.setHours(minTime, 0, 0, 0);
  }
  if (maxTime) {
    max.setHours(maxTime, 0, 0, 0);
  }

  return t < min || t > max;
}
</script>

<template>
  <TimePickerDropdown
    v-model="selectedTimeFormatted"
    :options="availableTimes"
    :label="label"
    searchable
    :search-field="(t: string) => t"
    :placeholder="defaultText"
    :show-empty-msg="availableTimes.length === 0"
    :error="error"
    :disabled="disabled"
    :default-value="defaultValue"
    class="w-[80px]"
    @search-value="(el: any) => (search = el)"
  >
    <template #selected="{ selected }">
      <span class="flex" :class="selected !== '00:00' ? 'justify-center' : 'justify-end pr-2'">
        {{ selected !== "00:00" ? selected : defaultText }}
      </span>
    </template>
    <template #option="{ option }">
      <span class="flex" :class="option !== '00:00' ? 'justify-center' : 'justify-end pr-2'">
        {{ option !== "00:00" ? option : defaultText }}
      </span>
    </template>
  </TimePickerDropdown>
  <span v-if="error && errorMessage" class="text-primary-red-color w-auto text-right">
    {{ errorMessage }}
  </span>
</template>

<style lang="scss" scoped></style>
